/**
 * Post-login location gate for the US-only signup policy.
 *
 * Email+password signups declare their state on /register, but
 * Google-OAuth accounts have no location at signup time. This gate:
 *   1. checks the authoritative /auth/me for `country`
 *   2. if missing AND the platform `us_only` flag is on, shows a
 *      blocking dialog asking the user to declare their location
 *   3. US → saved, gate closes. Outside US → account is marked
 *      region_restricted and the user sees the US-only notice with
 *      an international-waitlist capture + sign out.
 *
 * Mounted once inside AppShell so every authenticated page is covered.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { GlobeHemisphereWestIcon as GlobeHemisphereWest, EnvelopeSimpleIcon as EnvelopeSimple } from "@phosphor-icons/react";
import { US_STATES, OUTSIDE_US } from "@/lib/us-states";
import OptionListPicker from "@/components/OptionListPicker";

export default function LocationGate() {
  const { user, logout } = useAuth() || {};
  const [me, setMe] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistCountry, setWaitlistCountry] = useState("");
  const [waitlistDone, setWaitlistDone] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/public/signup-policy").then((r) => setPolicy(r.data)).catch(() => {});
  }, [user]);

  if (!user || !me || !policy || !policy.us_only) return null;

  const restricted = !!me.region_restricted;
  const needsDeclaration = !restricted && !me.country;
  if (!restricted && !needsDeclaration) return null;

  const save = async () => {
    if (!choice) return;
    setBusy(true);
    try {
      const isUs = choice !== OUTSIDE_US;
      const { data } = await api.post("/auth/me/location", {
        country: isUs ? "US" : "XX",
        us_state: isUs ? choice : undefined,
      });
      if (data.region_restricted) {
        setMe({ ...me, country: data.country, region_restricted: true });
      } else {
        setMe({ ...me, country: "US", region_restricted: false });
        toast.success("Location confirmed — welcome!");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save location");
    } finally {
      setBusy(false);
    }
  };

  const joinWaitlist = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/international-waitlist", {
        email: waitlistEmail || me.email,
        country: waitlistCountry || "Unknown",
      });
      setWaitlistDone(true);
    } catch {
      toast.error("Could not join the waitlist — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="location-gate"
    >
      <div className="bg-white rounded-2xl border border-[#E5E5E5] shadow-2xl max-w-md w-full p-7">
        {restricted ? (
          <div data-testid="region-restricted-screen">
            <div className="bg-gold/10 border border-gold/30 rounded-xl p-2.5 w-fit mb-4">
              <GlobeHemisphereWest size={24} weight="duotone" className="text-[#8A6A00]" />
            </div>
            <h2 className="font-spectral text-2xl tracking-tight">We&rsquo;re US-only for now</h2>
            <p className="text-sm text-[#6B6B6B] mt-2 leading-relaxed">
              {policy.us_only_message}
            </p>
            {waitlistDone ? (
              <p className="mt-5 text-sm font-semibold text-emerald-700" data-testid="waitlist-done">
                You&rsquo;re on the list — we&rsquo;ll email you the moment we launch in your country.
              </p>
            ) : (
              <form onSubmit={joinWaitlist} className="mt-5 space-y-2.5">
                <input
                  type="email"
                  required
                  value={waitlistEmail}
                  onChange={(e) => setWaitlistEmail(e.target.value)}
                  placeholder={me.email || "you@example.com"}
                  className="w-full rounded-lg border border-[#E5E5E5] px-3 py-2.5 text-sm"
                  data-testid="waitlist-email-input"
                />
                <input
                  type="text"
                  required
                  value={waitlistCountry}
                  onChange={(e) => setWaitlistCountry(e.target.value)}
                  placeholder="Your country"
                  className="w-full rounded-lg border border-[#E5E5E5] px-3 py-2.5 text-sm"
                  data-testid="waitlist-country-input"
                />
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full rounded-full bg-ink hover:bg-crimson text-white"
                  data-testid="waitlist-submit-btn"
                >
                  <EnvelopeSimple size={16} className="mr-2" />
                  Notify me when you launch in my country
                </Button>
              </form>
            )}
            <button
              onClick={logout}
              className="mt-4 text-xs text-[#6B6B6B] underline underline-offset-2"
              data-testid="restricted-signout-btn"
            >
              Sign out
            </button>
          </div>
        ) : (
          <div data-testid="location-declare-screen">
            <div className="bg-ink/5 border border-[#EAEAEA] rounded-xl p-2.5 w-fit mb-4">
              <GlobeHemisphereWest size={24} weight="duotone" className="text-ink" />
            </div>
            <h2 className="font-spectral text-2xl tracking-tight">Where are you located?</h2>
            <p className="text-sm text-[#6B6B6B] mt-2 leading-relaxed">
              Armstrong K9 Registry currently serves customers in the United
              States. We need your location to finish setting up your account.
            </p>
            <div className="mt-5">
              <OptionListPicker
                options={[...US_STATES, [OUTSIDE_US, "Outside the United States"]]}
                value={choice}
                onChange={setChoice}
                placeholder="Type to find your state…"
                testId="location-gate-select"
                maxHeight="max-h-48"
              />
            </div>
            <Button
              onClick={save}
              disabled={!choice || busy}
              className="mt-4 w-full rounded-full bg-ink hover:bg-crimson text-white"
              data-testid="location-gate-save-btn"
            >
              {busy ? "Saving…" : "Continue"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
